// Task tray and read only drawer for Revert VM runs. See revertRuns.spec.md (RVR-FR-002, 006, 007)
// See RevertVmTray.spec.md for RVT-FR-001, RVT-FR-002, RVT-FR-003, RVT-FR-004, RVT-FR-005
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import TaskTray from 'core/components/task-tray/TaskTray'
import ModalForm from 'core/elements/modal/ModalForm'
import RevertRunTracker from './RevertRunTracker'
import { RevertVmRunnerProvider, useRevertVmRunnerContext } from './RevertVmRunnerContext'
import { isRunActive, trayItem } from './revertRuns'

// Mounted once, app wide, so runs and their polling outlive the drawer that started them
export const RevertVmFeature = ({ children }: { children: ReactNode }) => (
  <RevertVmRunnerProvider>
    {children}
    <RevertVmTray />
  </RevertVmRunnerProvider>
)

// RVR-FR-006: drawers hide the tray while open
export const useHideTrayWhileOpen = () => {
  const { setDrawerOpen } = useRevertVmRunnerContext()
  useEffect(() => {
    setDrawerOpen(true)
    return () => setDrawerOpen(false)
  }, [setDrawerOpen])
}

// Opened from "Show details" on a tray card
export const RevertRunDrawer = ({ runId, onClose }: { runId: string; onClose: () => void }) => {
  const { runs } = useRevertVmRunnerContext()
  useHideTrayWhileOpen()
  const run = runs.find((candidate) => candidate.id === runId)
  return (
    <ModalForm
      open
      panel="drawer"
      title="Revert VM to Snapshot"
      onClose={onClose}
      closeText="Close"
      hideSubmit
      maxWidth={800}
    >
      {run && <RevertRunTracker run={run} />}
    </ModalForm>
  )
}

// RVR-FR-002: one card per run, newest first. X dismisses every ended run.
export const RevertVmTray = () => {
  const { runs, dismissRuns, drawerOpen } = useRevertVmRunnerContext()
  const [openRunId, setOpenRunId] = useState<string | null>(null)
  // Ids of the active runs the user hid with X. A new run changes the key, so the tray shows
  // again. Hiding never stops the runs.
  const [hiddenKey, setHiddenKey] = useState<string | null>(null)
  const activeKey = runs
    .filter(isRunActive)
    .map((run) => run.id)
    .join()

  return (
    <>
      {!drawerOpen && !(activeKey && activeKey === hiddenKey) && (
        <TaskTray
          title="Revert VM to Snapshot"
          items={runs.map(trayItem)}
          onAction={(item) => setOpenRunId(item.id)}
          onDismiss={() => {
            dismissRuns(runs.map((run) => run.id))
            setHiddenKey(activeKey)
          }}
        />
      )}
      {openRunId && <RevertRunDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />}
    </>
  )
}

export default RevertVmFeature
